import React, { FC, ReactNode, useMemo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ItemTemplate, TemplateElement, ViewElement } from '@mobileTypes'
import {
  Delete,
  DragIndicator,
  Edit,
  ExpandLess,
  ExpandMore,
  Image,
  TextFields,
  ViewModule,
} from '@mui/icons-material'
import { Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'


interface ConfiguredTemplatePanelProps {
  template: ItemTemplate
  onEditElement: (elementPath: string[]) => void
  onDeleteElement: (elementPath: string[]) => void
  activeId: string | null
  overId: string | null
  insertAfter: boolean
  activeElement: TemplateElement | null
  expandedItems: Set<string>
  onToggleExpanded: (elementPath: string[]) => void
}

const ELEMENT_ICONS: Record<string, ReactNode> = {
  view: <ViewModule fontSize='small' />,
  icon: <Image fontSize='small' />,
  field: <TextFields fontSize='small' />,
}

const getElementSummary = (element: TemplateElement): string => {
  switch (element.type) {
    case 'view':
      return 'View Container'
    case 'icon':
      return element.value || 'Icon'
    case 'field':
      return element.code || element.title || 'Field'
    default:
      return 'Unknown'
  }
}

interface TemplateElementItemProps {
  element: TemplateElement
  elementPath: string[]
  index: number
  onEdit: () => void
  onDelete: () => void
  onEditElement: (elementPath: string[]) => void
  onDeleteElement: (elementPath: string[]) => void
  isNested?: boolean
  isMainContainer?: boolean
  activeId: string | null
  overId: string | null
  insertAfter: boolean
  activeElement: TemplateElement | null
  allItems: TemplateElement[]
  expandedItems: Set<string>
  onToggleExpanded: (elementPath: string[]) => void
}

const TemplateElementItem: FC<TemplateElementItemProps> = ({
  element,
  elementPath,
  index,
  onEdit,
  onDelete,
  onEditElement,
  onDeleteElement,
  isNested = false,
  isMainContainer = false,
  activeId,
  overId,
  insertAfter,
  activeElement,
  allItems,
  expandedItems,
  onToggleExpanded,
}) => {
  const pathKey = elementPath.join(':')
  const expanded = expandedItems.has(pathKey)
  const isViewElement = element.type === 'view'
  const hasChildren = isViewElement && element.children && element.children.length > 0
  const draggableId = `ELEMENT:${pathKey}`
  const parentId = elementPath.length > 1 ? elementPath.slice(0, -1).join(':') : undefined

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
    useSortable({
      id: draggableId,
      disabled: isMainContainer || activeId === draggableId,
      data: {
        id: elementPath.join(':'),
        type: 'item',
        parent: parentId,
      },
      transition: null,
    })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: 'none', // Полностью отключаем анимацию
    opacity: activeId !== null && draggableId === activeId ? 0.3 : 1,
  }

  return (
    <>
      <Box ref={setNodeRef} sx={{ ...style, mb: 1 }}>
        <Paper
          variant='outlined'
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            ml: isNested ? 2 : 0,
            backgroundColor: 'background.paper',
            borderColor: 'divider',
            cursor: isMainContainer ? 'default' : 'grab',
            '&:active': {
              cursor: isMainContainer ? 'default' : 'grabbing',
            },
          }}
        >
          {!isMainContainer && (
            <Box
              ref={setActivatorNodeRef}
              {...listeners}
              {...attributes}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'grab',
                color: 'text.secondary',
              }}
            >
              <DragIndicator fontSize='small' />
            </Box>
          )}
          {isViewElement && (
            <IconButton size='small' onClick={() => onToggleExpanded(elementPath)} sx={{ p: 0.5 }}>
              {expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
            </IconButton>
          )}
          <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            {ELEMENT_ICONS[element.type]}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant='body2' fontWeight='500' noWrap>
                {getElementSummary(element)}
              </Typography>
              <Chip label={element.type} size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>
            {element.type === 'field' && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.objectCode || 'No object'}
              </Typography>
            )}
            {element.type === 'icon' && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.isJS ? 'JS Icon' : 'Static Icon'}
              </Typography>
            )}
            {element.type === 'view' && element.style && (
              <Typography variant='caption' color='text.secondary' noWrap>
                {element.style.flexDirection || 'column'} layout
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title='Edit'>
              <IconButton size='small' onClick={onEdit}>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
            {!isMainContainer && (
              <Tooltip title='Delete'>
                <IconButton size='small' color='error' onClick={onDelete}>
                  <Delete fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Дети рендерятся ОТДЕЛЬНО, вне драггабл контейнера */}
      {isViewElement && expanded && activeId !== draggableId && (
        <Box sx={{ ml: 2 }}>
          <Sortable
            items={(element as ViewElement).children || []}
            parentId={elementPath.join(':')}
            activeId={activeId}
            overId={overId}
            insertAfter={insertAfter}
            activeElement={activeElement}
            allItems={allItems}
            onEditElement={onEditElement}
            onDeleteElement={onDeleteElement}
            expandedItems={expandedItems}
            onToggleExpanded={onToggleExpanded}
          />
        </Box>
      )}
    </>
  )
}

interface SortableProps {
  items: TemplateElement[]
  parentId?: string
  activeId: string | null
  overId: string | null
  insertAfter: boolean
  activeElement: TemplateElement | null
  allItems: TemplateElement[]
  onEditElement: (elementPath: string[]) => void
  onDeleteElement: (elementPath: string[]) => void
  expandedItems: Set<string>
  onToggleExpanded: (elementPath: string[]) => void
}

const Sortable: FC<SortableProps> = ({
  items,
  parentId,
  activeId,
  overId,
  insertAfter,
  activeElement,
  allItems,
  onEditElement,
  onDeleteElement,
  expandedItems,
  onToggleExpanded,
}) => {
  const droppableId = parentId === undefined ? 'ROOT' : parentId
  const emptyItemId = `droppable-${droppableId}`
  const isRootLevel = parentId === undefined

  // На ROOT уровне может быть только один главный контейнер
  // Если он уже есть, не показываем EmptySortableItem
  const showEmptyItem = items.length === 0 || !isRootLevel

  const sortableItems = [
    ...items.filter(Boolean).map((_, index) => {
      const elementPath = parentId ? [...parentId.split(':'), index.toString()] : [index.toString()]

      return `ELEMENT:${elementPath.join(':')}`
    }),
    ...(items.length === 0 && showEmptyItem ? [emptyItemId] : []),
  ]

  // Find the index where the ghost element should be displayed
  const getGhostInsertIndex = (): number | null => {
    if (!overId || !activeId || !activeElement) return null

    // Find the index of the active element in this list
    const activeIndex = items.findIndex((_, idx) => {
      const path = parentId ? [...parentId.split(':'), idx.toString()] : [idx.toString()]
      return `ELEMENT:${path.join(':')}` === activeId
    })

    // Check if overId matches any existing element
    const overIndex = items.findIndex((_, idx) => {
      const path = parentId ? [...parentId.split(':'), idx.toString()] : [idx.toString()]
      return `ELEMENT:${path.join(':')}` === overId
    })

    if (overIndex !== -1) {
      // If dragging within the same list and overIndex is the same as activeIndex, don't show ghost
      if (activeIndex !== -1 && overIndex === activeIndex) {
        return null
      }
      
      // Use insertAfter flag to determine position
      let insertIndex = insertAfter ? overIndex + 1 : overIndex
      
      // When dragging within the same list, we don't hide the active element anymore
      // So we can use the insertIndex directly without adjustment
      return insertIndex
    }

    // If overId doesn't match any element, check if it's a droppable area
    if (overId.startsWith('droppable-')) {
      // If dragging within the same list, don't show ghost at the end if active element is already there
      if (activeIndex !== -1 && activeIndex === items.length - 1) {
        return null
      }
      // Insert at the end
      return items.length
    }

    return null
  }

  // Find the index of the active element to hide it when dragging within the same list
  const activeElementIndex = activeId
    ? items.findIndex((_, idx) => {
        const path = parentId ? [...parentId.split(':'), idx.toString()] : [idx.toString()]
        return `ELEMENT:${path.join(':')}` === activeId
      })
    : -1

  const ghostInsertIndex = getGhostInsertIndex()
  
  // Don't show ghost if it would be at the same position as the hidden active element
  const shouldShowGhost = ghostInsertIndex !== null && 
    (activeElementIndex === -1 || ghostInsertIndex !== activeElementIndex)

  // Always reserve space for ghost element at the end when dragging to prevent layout shift
  const shouldReserveGhostSpace = activeId && activeElement && ghostInsertIndex === items.length

  return (
    <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
      <Box sx={{ pl: 2, pt: items.length ? 1 : 0, pb: 2 }}>
        {items.map((item, index) => {
          const elementPath = parentId
            ? [...parentId.split(':'), index.toString()]
            : [index.toString()]
          const isMain = index === 0 && !parentId && item.type === 'view'
          const draggableId = `ELEMENT:${elementPath.join(':')}`
          const isActiveElement = activeId === draggableId

          return (
            <React.Fragment key={elementPath.join(':')}>
              {/* Show ghost element before this item if insert position is here */}
              {ghostInsertIndex === index && shouldShowGhost && activeElement && (
                <Box sx={{ mb: 1, opacity: 0.5 }}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      ml: parentId ? 2 : 0,
                      backgroundColor: 'background.paper',
                      borderColor: 'divider',
                    }}
                  >
                    <DragIndicator fontSize='small' sx={{ color: 'text.disabled' }} />
                    {activeElement.type === 'view' && (
                      <IconButton size='small' disabled sx={{ p: 0.5 }}>
                        <ExpandMore fontSize='small' />
                      </IconButton>
                    )}
                    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      {ELEMENT_ICONS[activeElement.type]}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant='body2' fontWeight='500' noWrap>
                          {getElementSummary(activeElement)}
                        </Typography>
                        <Chip label={activeElement.type} size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                      {activeElement.type === 'field' && (
                        <Typography variant='caption' color='text.secondary' noWrap>
                          {(activeElement as any).objectCode || 'No object'}
                        </Typography>
                      )}
                      {activeElement.type === 'icon' && (
                        <Typography variant='caption' color='text.secondary' noWrap>
                          {(activeElement as any).isJS ? 'JS Icon' : 'Static Icon'}
                        </Typography>
                      )}
                      {activeElement.type === 'view' && (activeElement as any).style && (
                        <Typography variant='caption' color='text.secondary' noWrap>
                          {((activeElement as any).style.flexDirection || 'column')} layout
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}
              {/* Show original element, but don't show ghost if it's at the same position */}
              {!(ghostInsertIndex === index && shouldShowGhost && activeElementIndex === index) && (
                <Box sx={{ mb: items.length ? 1 : 0 }}>
                  <TemplateElementItem
                    element={item}
                    elementPath={elementPath}
                    index={index}
                    onEdit={() => onEditElement(elementPath)}
                    onDelete={() => onDeleteElement(elementPath)}
                    onEditElement={onEditElement}
                    onDeleteElement={onDeleteElement}
                    isNested={!!parentId}
                    isMainContainer={isMain}
                    activeId={activeId}
                    overId={overId}
                    insertAfter={insertAfter}
                    activeElement={activeElement}
                    allItems={allItems}
                    expandedItems={expandedItems}
                    onToggleExpanded={onToggleExpanded}
                  />
                </Box>
              )}
            </React.Fragment>
          )
        })}
        {/* Always reserve space for ghost at the end when dragging to prevent layout shift */}
        {shouldReserveGhostSpace && (
          <Box sx={{ mb: 1, minHeight: '60px' }}>
            {shouldShowGhost && activeElement ? (
              <Box sx={{ opacity: 0.5 }}>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    ml: parentId ? 2 : 0,
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                  }}
                >
                  <DragIndicator fontSize='small' sx={{ color: 'text.disabled' }} />
                  {activeElement.type === 'view' && (
                    <IconButton size='small' disabled sx={{ p: 0.5 }}>
                      <ExpandMore fontSize='small' />
                    </IconButton>
                  )}
                  <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    {ELEMENT_ICONS[activeElement.type]}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant='body2' fontWeight='500' noWrap>
                        {getElementSummary(activeElement)}
                      </Typography>
                      <Chip label={activeElement.type} size='small' sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Box>
                    {activeElement.type === 'field' && (
                      <Typography variant='caption' color='text.secondary' noWrap>
                        {(activeElement as any).objectCode || 'No object'}
                      </Typography>
                    )}
                    {activeElement.type === 'icon' && (
                      <Typography variant='caption' color='text.secondary' noWrap>
                        {(activeElement as any).isJS ? 'JS Icon' : 'Static Icon'}
                      </Typography>
                    )}
                    {activeElement.type === 'view' && (activeElement as any).style && (
                      <Typography variant='caption' color='text.secondary' noWrap>
                        {((activeElement as any).style.flexDirection || 'column')} layout
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>
            ) : (
              // Reserve invisible space when ghost should be shown but isn't yet
              <Box sx={{ visibility: 'hidden' }}>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    ml: parentId ? 2 : 0,
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ width: 24, height: 24 }} />
                  <Box sx={{ width: 24, height: 24 }} />
                  <Box sx={{ flex: 1, minWidth: 0, height: 40 }} />
                </Paper>
              </Box>
            )}
          </Box>
        )}
        {/* Invisible droppable zone for empty lists */}
        {items.length === 0 && showEmptyItem && (
          <InvisibleDroppableZone parentId={parentId} activeId={activeId} />
        )}
      </Box>
    </SortableContext>
  )
}

// Invisible droppable zone for empty lists (replaces EmptySortableItem)
const InvisibleDroppableZone: FC<{ parentId?: string; activeId: string | null }> = ({ parentId, activeId }) => {
  const droppableId = parentId === undefined ? 'ROOT' : parentId
  const id = `droppable-${droppableId}`

  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id,
    data: {
      id: droppableId,
      type: 'droppable',
      parent: parentId,
    },
    transition: null,
    disabled: activeId !== null && activeId === id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: 'none',
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...style,
        // Делает зону дропа в пустом контейнере достаточно высокой,
        // чтобы на нее было удобно наводиться, но без визуального шума
        minHeight: 40,
        width: '100%',
        mt: 0.5,
      }}
      {...listeners}
      {...attributes}
    />
  )
}

export const ConfiguredTemplatePanel: FC<ConfiguredTemplatePanelProps> = ({
  template,
  onEditElement,
  onDeleteElement,
  activeId,
  overId,
  insertAfter,
  activeElement,
  expandedItems,
  onToggleExpanded,
}) => {
  const rootElements = useMemo(() => template.children || [], [template])
  const isMainContainer = rootElements.length > 0 && rootElements[0]?.type === 'view'

  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Configured Template</Typography>
        <Typography variant='caption' color='text.secondary'>
          Reorder elements by dragging
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, pb: 4 }}>
        <Sortable
          items={rootElements}
          parentId={undefined}
          activeId={activeId}
          overId={overId}
          insertAfter={insertAfter}
          activeElement={activeElement}
          allItems={rootElements}
          onEditElement={onEditElement}
          onDeleteElement={onDeleteElement}
          expandedItems={expandedItems}
          onToggleExpanded={onToggleExpanded}
        />
      </Box>
    </Paper>
  )
}
